import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import BackButton from '../components/BackButton';
import Footer from '../components/Footer';
import { items } from '../data/lengthItems';
import '../App.scss';

interface Item {
  name: string;
  length: number;
  unit: string;
  category: string;
  image?: string;
}

interface PexelsPhoto {
  src: {
    medium: string;
  };
}

interface GameState {
  currentItem: Item;
  currentItemIndex: number;
  score: number;
  highScore: number;
  isGameOver: boolean;
  selectedUnit: string | null;
  showCorrectTooltip: boolean;
  isImageLoading: boolean;
}

const PEXELS_API_KEY = 'peevFGc8LJLNK2nHZUgFqC2m8v4Fku41UKC1KwoWC2zHqLqSh9UIs15X';
const SHOWN_ITEMS_KEY = 'lengthUnitsShownItems';

function getShownItems(): number[] {
  const stored = localStorage.getItem(SHOWN_ITEMS_KEY);
  return stored ? JSON.parse(stored) : [];
}

function saveShownItems(indices: number[]) {
  localStorage.setItem(SHOWN_ITEMS_KEY, JSON.stringify(indices));
}

function getRandomItem(): { index: number; item: Item } {
  let shownItems = getShownItems();
  
  // If we've shown 500 items (50% of total), reset the list
  if (shownItems.length >= 500) {
    shownItems = [];
    localStorage.removeItem(SHOWN_ITEMS_KEY);
  }

  // Get all available indices that haven't been shown
  const availableIndices = Array.from({ length: items.length }, (_, i) => i)
    .filter(i => !shownItems.includes(i));

  // Pick a random index from available ones
  const randomIndex = availableIndices[Math.floor(Math.random() * availableIndices.length)];

  // Add the new index to shown items and save
  shownItems.push(randomIndex);
  saveShownItems(shownItems);

  return {
    index: randomIndex,
    item: items[randomIndex]
  };
}

function getSearchTermForItem(item: Item): string {
  // Add 'macro' for very small items, 'aerial' for very large items
  if (item.unit === 'mm') {
    return `${item.name} macro`;
  } else if (item.unit === 'km') {
    return `${item.name} aerial`;
  }
  
  // Skip images for abstract measurements
  if (item.name.includes('Thickness') || 
      item.name.includes('Diameter') || 
      item.name.includes('Width')) {
    return '';
  }

  return item.name;
}

const LengthUnits: React.FC = () => {
  const navigate = useNavigate();
  const [gameState, setGameState] = useState<GameState>(() => {
    const savedHighScore = localStorage.getItem('lengthUnitsHighScore');
    const { index, item } = getRandomItem();
    
    return {
      currentItem: item,
      currentItemIndex: index,
      score: 0,
      highScore: savedHighScore ? parseInt(savedHighScore) : 0,
      isGameOver: false,
      selectedUnit: null,
      showCorrectTooltip: false,
      isImageLoading: true,
    };
  });

  const fetchPexelsImage = async (item: Item) => {
    const searchTerm = getSearchTermForItem(item);
    console.log('Search term:', searchTerm);
    
    if (!searchTerm) {
      console.log('No search term defined for:', item.name);
      setGameState(prev => ({
        ...prev,
        currentItem: {
          ...prev.currentItem,
          image: undefined
        },
        isImageLoading: false
      }));
      return;
    }

    try {
      const response = await fetch(
        `https://api.pexels.com/v1/search?query=${encodeURIComponent(searchTerm)}&per_page=1&orientation=landscape&size=medium`, 
        {
          headers: {
            'Authorization': PEXELS_API_KEY,
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (!response.ok) {
        console.error('Pexels API error:', response.status, response.statusText);
        throw new Error('Failed to fetch image');
      }

      const data = await response.json();
      console.log('Pexels API response:', data);
      
      if (data.photos && data.photos.length > 0) {
        const imageUrl = data.photos[0].src.medium;
        console.log('Setting image URL:', imageUrl);
        setGameState(prev => ({
          ...prev,
          currentItem: {
            ...prev.currentItem,
            image: imageUrl
          },
          isImageLoading: false
        }));
      } else {
        console.log('No images found for:', searchTerm);
        setGameState(prev => ({
          ...prev,
          currentItem: {
            ...prev.currentItem,
            image: undefined
          },
          isImageLoading: false
        }));
      }
    } catch (error) {
      console.error('Error fetching image:', error);
      setGameState(prev => ({
        ...prev,
        currentItem: {
          ...prev.currentItem,
          image: undefined
        },
        isImageLoading: false
      }));
    }
  };

  function handleUnitClick(unit: string) {
    if (gameState.isGameOver || gameState.selectedUnit) return;

    const isCorrect = unit === gameState.currentItem.unit;
    setGameState(prev => ({
      ...prev,
      selectedUnit: unit,
      showCorrectTooltip: isCorrect,
      isGameOver: !isCorrect
    }));

    if (isCorrect) {
      // Show correct tooltip for 2 seconds, then move to next question
      setTimeout(() => {
        const newScore = gameState.score + 1;
        const newHighScore = Math.max(newScore, gameState.highScore);
        
        if (newHighScore > gameState.highScore) {
          localStorage.setItem('lengthUnitsHighScore', newHighScore.toString());
        }

        // Get next random item and reset all states
        const { index, item } = getRandomItem();
        setGameState({
          currentItem: item,
          currentItemIndex: index,
          score: newScore,
          highScore: newHighScore,
          selectedUnit: null,
          showCorrectTooltip: false,
          isGameOver: false,
          isImageLoading: true,
        });
      }, 2000);
    }
  }

  function handleRetry() {
    // Clear shown items when starting a new game
    localStorage.removeItem(SHOWN_ITEMS_KEY);
    const { index, item } = getRandomItem();

    setGameState({
      currentItem: item,
      currentItemIndex: index,
      score: 0,
      highScore: gameState.highScore,
      isGameOver: false,
      selectedUnit: null,
      showCorrectTooltip: false,
      isImageLoading: true,
    });
  }

  useEffect(() => {
    if (gameState.currentItem.name) {
      setGameState(prev => ({
        ...prev,
        isImageLoading: true
      }));
      console.log('Fetching image for:', gameState.currentItem.name);
      fetchPexelsImage(gameState.currentItem);
    }
  }, [gameState.currentItem.name]);

  // Separate component for the unit buttons to ensure clean remount
  const UnitButtons = ({ questionId }: { questionId: string }) => (
    <div className="unit-buttons">
      {['mm', 'cm', 'm', 'km'].map((unit) => {
        let buttonClass = '';
        
        // Only apply classes when showing result of current question
        if (gameState.selectedUnit) {
          if (gameState.isGameOver) {
            if (unit === gameState.selectedUnit) {
              buttonClass = 'incorrect-answer';
            } else if (unit === gameState.currentItem.unit) {
              buttonClass = 'correct-answer';
            }
          } else if (unit === gameState.selectedUnit) {
            buttonClass = 'correct-answer';
          }
        }

        return (
          <button 
            key={unit}
            onClick={() => !gameState.isGameOver && handleUnitClick(unit)}
            className={buttonClass}
            disabled={gameState.isGameOver || gameState.selectedUnit !== null}
          >
            {unit}
            {gameState.showCorrectTooltip && unit === gameState.selectedUnit && (
              <div className="tooltip">Correct!</div>
            )}
          </button>
        );
      })}
    </div>
  );

  return (
    <div className="length-units-game">
      <div className="game-header">
        <BackButton />
      </div>
      <h1 className="game-title">Length Units</h1>

      <div className="game-score">
        <div className={`high-score ${gameState.score >= gameState.highScore ? 'new-record' : ''}`}>
          <b>Score: {gameState.score}</b> | <span className="best-score">Best: {gameState.highScore}</span>
          {gameState.score >= gameState.highScore && gameState.score > 0 && (
            <span className="new-record-text">New Record!</span>
          )}
        </div>
      </div>
      
      <div className="game-container">
        <div className="item-container">
          <h3>What is the size of a {gameState.currentItem.name}?</h3>
          
          {gameState.currentItem.image && (
            <div className={`item-image ${gameState.isImageLoading ? 'loading' : ''}`}>
              <img 
                src={gameState.currentItem.image} 
                alt={`Size reference for ${gameState.currentItem.name}`}
                className={gameState.isImageLoading ? 'loading' : ''}
                onLoad={() => {
                  setGameState(prev => ({
                    ...prev,
                    isImageLoading: false
                  }));
                }}
                onError={() => {
                  setGameState(prev => ({
                    ...prev,
                    currentItem: {
                      ...prev.currentItem,
                      image: undefined
                    },
                    isImageLoading: false
                  }));
                }}
              />
            </div>
          )}

          {gameState.isGameOver && (
            <div className="try-again-container">
              <div className="try-again-message">Wrong Answer!</div>
              <button className="play-again-button" onClick={handleRetry}>
                Play Again
              </button>
            </div>
          )}
          
          <div className="measurement-container">
            <div className="item-length">{gameState.currentItem.length}</div>
            {/* Key prop forces complete remount of buttons with each new question */}
            <UnitButtons key={`question-${gameState.currentItemIndex}`} questionId={`question-${gameState.currentItemIndex}`} />
          </div>
        </div>
      </div>
      
      <div className="game-instructions">
        <h3>How to Play:</h3>
        <p>Select the correct unit of measurement for each item.</p>
        <p>Keep playing as long as you get the answers right!</p>
        <p>The game ends if you choose an incorrect answer.</p>
      </div>
      <Footer />
    </div>
  );
};

export default LengthUnits; 